<template>
  <div v-if="canThisUserView" class="assignments-listing">
    <AssignmentsFilters
      v-if="!hideHeaders"
      :current-user="currentUser"
      :assignments-filters="assignmentsFilters"
      @change="onFiltersChange"
    />

    <TolAlert v-if="tolAlert" v-bind="tolAlert" />

    <AssignmentsActions
      v-if="!hideHeaders && showAssignmentsListingActions"
      :title-all-or-filtered="allOrFilteredTitle"
      :listing-options="currentListingOptions"
      @load-spinner="loadSpinner($event)"
    />
    <tol-table
      :table-data="assignments"
      :columns="renderColumns"
      :show-spinner="isLoading"
      :is-row-clickable="true"
      :class="{ small: isProfileListing }"
      @row-click="handleRowClick($event)"
    >
      <template v-slot:tableHeader>
        <th v-if="!hideCheckbox && showAssignmentsListingActions">
          <!-- checkbox -->
        </th>
        <template v-for="f in renderColumns">
          <th v-if="!sortableFields.includes(f)" :key="f">
            {{ getDisplayName(f) }}
          </th>
          <th-sortable
            v-else
            :key="getDisplayName(f)"
            :direction="currentSortField === f ? currentSortDirection : 'none'"
            :field-name="f"
            @change="onSortOrderChange"
            >{{ getDisplayName(f) }}</th-sortable
          >
        </template>
      </template>

      <template v-if="!hideCheckbox && showAssignmentsListingActions" v-slot:check="{ value }">
        <td>
          <Checkbox
            size="small"
            :checked="value.isSelected"
            :modified="value.referenceNumber in modifiedAssignments"
            @change="onToggleSelectAssignment(value.referenceNumber)"
          />
        </td>
      </template>

      <template v-slot:approvers="{ value }">
        <ol class="approvers">
          <li v-for="approver in value.approvers" :key="approver.id">
            {{ approver.lastName }}, {{ approver.firstName }}
          </li>
        </ol>
      </template>

      <template v-slot:entity="{ value }">
        {{ value.entity | capitalize }}
      </template>

      <template v-slot:startDate="{ value }">
        <font-awesome-icon icon="calendar-alt" />
        {{ value.startDate }}
      </template>

      <template v-slot:stopDate="{ value }">
        <font-awesome-icon icon="calendar-alt" />
        {{ value.stopDate }}
      </template>

      <template v-slot:status="{ value }">
        <Status :status="value.status" object-type="assignment" />
      </template>
    </tol-table>

    <Pagination
      :records-per-page="assignmentsPerPage"
      :offset="currentPage"
      :record-count="recordCount"
      :class="{ small: isProfileListing }"
      @change="onChangePaginationParams($event)"
    />
  </div>
</template>

<script>
import {
  AssignmentsListingActions,
  AssignmentsListingGetters
} from '@/app/features/assignments/features/assignments-listing/assignments-listing.keys';
import { getInitialState } from '@/app/features/assignments/features/assignments-listing/assignments-listing.store';
import TolAlert from '@/app/components/TolAlert';
import AssignmentsFilters from '@/app/features/assignments/features/assignments-listing/components/AssignmentsFilters';
import AssignmentsActions from '@/app/features/assignments/features/assignments-listing/components/AssignmentsActions';
import { AppActions } from '@/app/app.keys';
import TolTable from '@/app/components/TolTable';
import Status from '@/app/components/Status';
import Checkbox from '@/app/components/Checkbox';
import ThSortable from '@/app/components/ThSortable';
import Pagination from '@/app/components/Pagination';
import { equals } from 'ramda';

export default {
  name: 'AssignmentsListing',
  components: {
    TolTable,
    TolAlert,
    AssignmentsFilters,
    AssignmentsActions,
    Status,
    Checkbox,
    ThSortable,
    Pagination
  },
  props: {
    hideHeaders: {
      type: Boolean,
      default: false
    },
    displayColumns: {
      type: Array,
      default: () => []
    },
    hideCheckbox: {
      type: Boolean,
      default: false
    },
    isProfileListing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sortableFields: [
        'payrollNumber',
        'contractorName',
        'branch',
        'entity',
        'client',
        'clientName',
        'startDate',
        'stopDate',
        'status'
      ]
    };
  },
  computed: {
    isLoading() {
      return this.$store.getters[AssignmentsListingGetters.AssignmentIsLoading];
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    canThisUserView() {
      return this.currentUser ? this.currentUser.permissions['ASSIGNMENT']['VIEW'] : false;
    },
    tolAlert() {
      return false;
    },
    assignments() {
      return this.$store.getters[AssignmentsListingGetters.AssignmentsList];
    },
    assignmentsPerPage() {
      return this.$store.getters[AssignmentsListingGetters.AssignmentsPerPage];
    },
    currentPage() {
      return this.$store.getters[AssignmentsListingGetters.AssignmentsListingOffset];
    },
    recordCount() {
      return this.$store.getters[AssignmentsListingGetters.AssignmentsFilteredRecordCount];
    },
    assignmentsFilters() {
      return this.$store.getters[AssignmentsListingGetters.AssignmentsFilters];
    },
    modifiedAssignments() {
      return this.$store.getters[AssignmentsListingGetters.ModifiedAssignments];
    },
    currentSortField() {
      return this.$store.getters[AssignmentsListingGetters.SortBy];
    },
    currentSortDirection() {
      return this.$store.getters[AssignmentsListingGetters.SortDirection];
    },
    renderColumns() {
      return this.displayColumns.length > 0 ? this.displayColumns : this.columns;
    },
    columns() {
      return [
        'payrollNumber',
        'contractorName',
        'clientName',
        'approvers',
        'startDate',
        'stopDate',
        'branch',
        'status'
      ];
    },
    allOrFilteredTitle() {
      return this.areAnyFiltersSet()
        ? 'assignments.listing.actions.titleFiltered'
        : 'assignments.listing.actions.titleAll';
    },
    showAssignmentsListingActions() {
      let role = this.currentUser.roles.find(
        role =>
          role.role_name == 'Payroll' ||
          role.role_name == 'Payroll Perm' ||
          role.role_name === 'System Admin'
      );
      return role ? true : false;
    },
    currentListingOptions() {
      return {
        isProfileListing: this.isProfileListing,
        referenceNumber: this.currentUser.referenceNumber
      };
    }
  },
  async mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('assignments.listing.title'));
    if (this.currentUser === null) {
      await this.$store.dispatch(AppActions.RetrieveSession);
    }
    this.$store.dispatch(AssignmentsListingActions.RetrieveAssignments, {
      isProfileListing: this.isProfileListing,
      referenceNumber: this.currentUser.referenceNumber
    });
  },
  methods: {
    getDisplayName(name) {
      let nameMapping = {
        payrollNumber: 'Payroll #',
        contractorName: 'Contractor',
        contractor: 'Contractor',
        clientName: 'Client',
        client: 'Client',
        entity: 'Entity',
        approvers: 'Approvers',
        startDate: 'Start',
        stopDate: 'Stop',
        branch: 'Branch',
        status: 'Status'
      };
      return nameMapping[name];
    },
    onFiltersChange(assignmentsFilters) {
      this.$store.dispatch(AssignmentsListingActions.SetAssignmentsFilters, assignmentsFilters);
    },
    onSortOrderChange(sortOrder) {
      this.$store.dispatch(AssignmentsListingActions.SetAssignmentsSortOrder, {
        sortOrder,
        isProfileListing: this.isProfileListing
      });
    },
    onToggleSelectAssignment(referenceNumber) {
      this.$store.dispatch(AssignmentsListingActions.SelectAssignment, referenceNumber);
    },
    onChangePaginationParams(event) {
      this.$store.dispatch(AssignmentsListingActions.SetAssignmentsPaginationParameters, {
        event,
        isProfileListing: this.isProfileListing
      });
    },
    areAnyFiltersSet() {
      const initFilters = getInitialState().assignmentsFilters;
      const currentFilters = this.$store.getters[AssignmentsListingGetters.AssignmentsFilters];
      for (let prop in initFilters) {
        if (!equals(initFilters[prop], currentFilters[prop])) return true;
      }
      return false;
    },
    handleRowClick(evt) {
      this.$router.push(`/assignments/${evt.referenceNumber}`);
    },
    loadSpinner(flag) {
      this.$store.dispatch(AssignmentsListingActions.SetAssignmentsLoading, flag);
    }
  }
};
</script>

<style lang="scss" scoped>
.assignments-listing {
  ol.approvers {
    list-style-position: inside;
    padding-left: 0;
  }
}
</style>
